/**
 * SubscriptionDropdown Component
 *
 * Header dropdown showing current plan, credits, and subscription status.
 * Opens SubscriptionModal on click.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, CircleUser, CreditCard, LogOut, ShieldCheck } from 'lucide-react';
import { stripeService } from '../../services';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface SubscriptionDropdownProps {
  userEmail?: string;
  onManageClick: () => void;
  onSignOut: () => void;
}

interface SubscriptionStatus {
  job_credits: number;
  has_subscription: boolean;
  subscription_status?: string;
  jobs_used_this_month?: number;
  jobs_limit_this_month?: number;
  documents_uploaded: number;
  documents_limit: number;
}

export const SubscriptionDropdown: React.FC<SubscriptionDropdownProps> = ({
  userEmail,
  onManageClick,
  onSignOut,
}) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatus();

    // Listen for subscription updates (from payment flows)
    const handleSubscriptionUpdate = () => {
      loadStatus();
    };
    window.addEventListener('subscription-updated', handleSubscriptionUpdate);

    return () => {
      window.removeEventListener('subscription-updated', handleSubscriptionUpdate);
    };
  }, []);

  const loadStatus = async () => {
    try {
      const data = await stripeService.getStatus();
      setStatus(data);
    } catch (error) {
      console.error('SubscriptionDropdown.loadStatus() failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanName = () => {
    if (!status) return 'Free';
    if (status.has_subscription) return 'Monthly';
    if (status.job_credits > 0) return 'Starter';
    return 'Free';
  };

  if (loading) {
    // Skeleton in the trigger's footprint — never a blank gap in the header
    return <div aria-hidden className="h-8 w-14 animate-pulse rounded-lg bg-muted" />;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button variant="ghost" aria-label="Account and subscription" />}
      >
        <span className="relative">
          <CircleUser className="size-5" />
          {/* Subscription badge - show remaining jobs this month */}
          {status && status.has_subscription && (
            <span className="absolute -top-2 -right-1.5 flex size-4 items-center justify-center rounded-full bg-success text-[10px] font-bold text-background">
              {(status.jobs_limit_this_month || 8) - (status.jobs_used_this_month || 0)}
            </span>
          )}
          {/* Credit badge - show available credits, only if no subscription */}
          {status && !status.has_subscription && status.job_credits > 0 && (
            <span className="absolute -top-2 -right-1.5 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {status.job_credits}
            </span>
          )}
        </span>
        <ChevronDown className="size-4 transition-transform group-aria-expanded/button:rotate-180" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72">
        {/* User Email */}
        {userEmail && (
          <>
            <div className="px-2.5 py-2">
              <p className="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                Account
              </p>
              <p className="mt-1 text-sm font-medium break-all text-foreground">{userEmail}</p>
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Plan Status */}
        {status && (
          <>
            <div className="space-y-2 px-2.5 py-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Current Plan</span>
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">
                  {getPlanName()}
                </span>
              </div>

              {status.has_subscription && (
                <p className="text-xs text-foreground/55 tabular-nums">
                  {status.jobs_used_this_month || 0}/{status.jobs_limit_this_month || 8} jobs this
                  month
                </p>
              )}

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Job Credits</span>
                <span className="text-lg font-bold text-foreground tabular-nums">
                  {status.job_credits}
                </span>
              </div>

              {/* Free credits message */}
              {!status.has_subscription && status.job_credits > 0 && (
                <p className="text-xs text-primary">
                  You have {status.job_credits} free job tailoring credit
                  {status.job_credits !== 1 ? 's' : ''}!
                </p>
              )}
              {!status.has_subscription && status.job_credits === 0 && (
                <p className="text-xs text-foreground/55">
                  Purchase credits to unlock all features
                </p>
              )}
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem onClick={onManageClick}>
          <CreditCard />
          Manage Subscription
        </DropdownMenuItem>
        {/* Consent center (spec 5.5 'Recruiters' settings section): always
            reachable so recruiter links can be reviewed/revoked anytime. */}
        <DropdownMenuItem onClick={() => navigate('/app/recruiters')}>
          <ShieldCheck />
          Recruiter Access
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onSignOut}>
          <LogOut />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
